import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { Instrument } from "../../sdk";
import { Skeleton } from "../../components/ui";
import { navigate } from "../../router";
import { useInstruments, useTradingState } from "../../state/market";
import { Pulse } from "./Feed";
import { LiveChart, RangePicker } from "./LiveChart";
import { MarketHeader } from "./MarketHeader";
import { Rides } from "./Rides";
import { CallCard, useMarketLeverage, useSlipKeys } from "./Slip";
import { useCall } from "./useCall";
import "./play.css";

/** An element's height and left padding, kept as they change. */
function useBox(el: RefObject<HTMLElement | null>) {
  const [box, setBox] = useState({ height: 0, padLeft: 16 });
  useLayoutEffect(() => {
    const node = el.current;
    if (!node) return;
    const fit = () => {
      const height = Math.round(node.getBoundingClientRect().height);
      const padLeft = Number.parseFloat(getComputedStyle(node).paddingLeft) || 0;
      setBox((last) => (last.height === height && last.padLeft === padLeft ? last : { height, padLeft }));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => observer.disconnect();
  }, [el]);
  return box;
}

/** The table: the chart is the screen, the price and the table's pulse laid over it, and the call
 * docked along the bottom. Your live calls scroll up under the dock. */
function Table({ instrument, instruments }: { instrument: Instrument; instruments: Instrument[] }) {
  const view = useCall(instrument);
  const dock = useRef<HTMLDivElement>(null);
  const head = useRef<HTMLDivElement>(null);
  useMarketLeverage(instrument);
  useSlipKeys(instrument);
  const dockBox = useBox(dock);
  const headBox = useBox(head);

  return (
    <main className="pl" style={{ ["--dock-h" as string]: `${dockBox.height}px` }}>
      <section className="pl-hero" aria-label={`${instrument.symbol} live`}>
        <LiveChart instrument={instrument} top={headBox.height + 32} gutter={headBox.padLeft} />
        <div className="pl-head" ref={head}>
          <MarketHeader instrument={instrument} instruments={instruments} aside={<RangePicker />} />
        </div>
        <div className="pl-foot">
          <Pulse />
        </div>
      </section>
      <div className="pl-below">
        <Rides />
      </div>
      <div className="pl-dock" ref={dock}>
        <div className="pl-dock-inner">
          <CallCard instrument={instrument} view={view} size="lg" />
        </div>
      </div>
    </main>
  );
}

export function Play({ coin }: { coin: string }) {
  const state = useTradingState();
  const instruments = useInstruments();
  const instrument = instruments.find((item) => item.symbol === coin);

  // an unknown market goes to the first listed one
  useEffect(() => {
    const first = instruments[0];
    if (instruments.length && !instrument && first) navigate(`/play/${first.symbol}`, true);
  }, [instruments, instrument]);

  if (!instrument) {
    return (
      <main className="pl">
        <div className="pl-loading">
          {state.isError ? (
            <p className="muted">The table did not answer. Retrying…</p>
          ) : (
            <>
              <Skeleton width="40%" height={40} />
              <Skeleton height={280} />
            </>
          )}
        </div>
      </main>
    );
  }
  return <Table instrument={instrument} instruments={instruments} />;
}
